import * as React from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { hrApi } from '../api';
import type { AttendanceSession } from '../types';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** The record being corrected. `null` renders nothing. */
  session: AttendanceSession | null;
  employeeName?: string;
  onSaved: () => void;
}

/** Combine a YYYY-MM-DD date with a HH:mm time into an ISO string (local TZ). */
function toIso(date: string, time: string): string | null {
  if (!date || !time) return null;
  const d = new Date(`${date}T${time}`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** Local YYYY-MM-DD / HH:mm halves of an ISO timestamp, for the date+time inputs. */
function splitLocal(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  const p = (n: number) => (n < 10 ? `0${n}` : String(n));
  return {
    date: `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`,
    time: `${p(d.getHours())}:${p(d.getMinutes())}`,
  };
}

/**
 * Correct one attendance record. The important case is the OPEN session: a
 * forgotten clock-out blocks every later clock-in, so this dialog closes it
 * (or deliberately re-opens a closed one via the checkbox).
 */
export function EditAttendanceDialog({ open, onOpenChange, session, employeeName, onSaved }: Props) {
  const [date, setDate] = React.useState('');
  const [inTime, setInTime] = React.useState('09:00');
  const [outDate, setOutDate] = React.useState('');
  const [outTime, setOutTime] = React.useState('17:00');
  const [stillOpen, setStillOpen] = React.useState(false);
  const [notes, setNotes] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (!open || !session) return;
    const inParts = splitLocal(session.clock_in_at);
    setDate(inParts.date);
    setInTime(inParts.time);
    if (session.clock_out_at) {
      const outParts = splitLocal(session.clock_out_at);
      setOutDate(outParts.date);
      setOutTime(outParts.time);
      setStillOpen(false);
    } else {
      // Prefill the clock-out with the clock-in day so closing an open session
      // is a two-keystroke job, not a date hunt.
      setOutDate(inParts.date);
      setOutTime('17:00');
      setStillOpen(true);
    }
    setNotes(session.notes ?? '');
  }, [open, session]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!session) return;
    const clockIn = toIso(date, inTime);
    if (!clockIn) { toast.error('Pick a valid date and clock-in time'); return; }
    let clockOut: string | null = null;
    if (!stillOpen) {
      clockOut = toIso(outDate || date, outTime);
      if (!clockOut) { toast.error('Pick a valid clock-out time'); return; }
    }
    setBusy(true);
    try {
      await hrApi.updateAttendanceSession(session.id, {
        clock_in_at: clockIn,
        clock_out_at: clockOut,
        notes: notes || null,
      });
      toast.success(clockOut ? 'Attendance updated' : 'Session left open');
      onSaved();
    } catch (err: any) {
      toast.error('Could not update entry', { description: err.message });
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit attendance entry{employeeName ? ` — ${employeeName}` : ''}</DialogTitle>
        </DialogHeader>
        {session && (
          <form onSubmit={submit} className="grid gap-4 pt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-1.5">
                <Label>Clock-in date</Label>
                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
              </div>
              <div className="grid gap-1.5">
                <Label>Clock in</Label>
                <Input type="time" value={inTime} onChange={(e) => setInTime(e.target.value)} required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-1.5">
                <Label>Clock-out date</Label>
                <Input type="date" value={outDate} onChange={(e) => setOutDate(e.target.value)} disabled={stillOpen} />
              </div>
              <div className="grid gap-1.5">
                <Label>Clock out</Label>
                <Input type="time" value={outTime} onChange={(e) => setOutTime(e.target.value)} disabled={stillOpen} />
              </div>
            </div>
            <label className="flex items-center gap-2.5 cursor-pointer text-[13px] text-muted-foreground">
              <input
                type="checkbox"
                checked={stillOpen}
                onChange={(e) => setStillOpen(e.target.checked)}
                className="h-4 w-4 rounded border-border accent-saffron"
              />
              Leave session open (no clock-out yet)
            </label>
            {stillOpen && (
              <p className="text-[12px] rounded-xl px-3 py-2 bg-coral-100 text-coral-700 ring-1 ring-coral-200 dark:bg-coral-500/15 dark:text-coral-200 dark:ring-coral-500/35">
                An open session blocks this employee from clocking in again — close it unless they are genuinely still on the clock.
              </p>
            )}
            <div className="grid gap-1.5">
              <Label>Notes</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="e.g. Forgot to clock out" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save changes'}</Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
